import { GameErrorCodes, LicenseType, ToGamePostMessage } from '../../../enums';
import { GameErrorPayload, LaunchData } from '../../../models';
import { RootStore } from '../../../store';
import { IntegrationBase } from '../base';

export class BetfairIntegration extends IntegrationBase {

  private gameClient: any = null;

  private needReconnect = false;

  constructor(rootStore: RootStore) {
    super(rootStore);
    // import betfair lib when integration is constructed
    import('../../../libs/betfair.js').then(() => {
      this.gameClient = window.com.ppb.gc;
      const { data: launchData } = this.rootStore.gameStore.launchData;
      if (launchData) {
        this.init(launchData);
      }
    });
  }

  public init(data: LaunchData): void {
    this.registerEventListeners();
  }

  private registerEventListeners(): void {

    this.gameClient.receiveBalanceChanged = () => {
      this.rootStore.postMessageStore.sendUpdateBalance();
    };

    this.gameClient.receiveSettingsUpdate = (settings: any) => {
      if (settings) {
        if (settings.settingName === 'sound') {
          this.rootStore.gameStore.handleSoundEnabledUpdate(!!settings.settingValue);
          this.rootStore.postMessageStore.sendUpdateSettings({ sound: settings.settingValue });
        } else {
          this.rootStore.postMessageStore.sendUpdateSettings({ fastPlay: settings.settingValue });
        }
      }
    };

    this.gameClient.receiveTogglePaytable = () => {
      this.rootStore.postMessageStore.sendTogglePayTable();
    };

    this.gameClient.receiveNotifyAction = (payload: any) => {
      if (payload?.actionName === 'gameHelp') {
        this.rootStore.postMessageStore.sendToggleGameHelp();
      }

      if (payload?.actionName === 'continue') {
        this.resumeGame();
      }

      if (payload?.actionName === 'errorMessageDismissed') {

        if (this.needReconnect) {
          this.resumeGame(200);
          this.needReconnect = false;
        }
      }

      if (payload?.actionName === 'realityCheckAck') {
        this.puaseGame();
      }
    };

    this.gameClient.receiveStopAutoSpin = () => {
      this.rootStore.postMessageStore.sendStopAutoSpins();
    };

    this.gameClient.registerCallbackFunction(window.com.ppb.constants.BALANCE_CHANGED, this.gameClient.receiveBalanceChanged);
    this.gameClient.registerCallbackFunction(window.com.ppb.constants.TOGGLE_PAYTABLE, this.gameClient.receiveTogglePaytable);
    this.gameClient.registerCallbackFunction(window.com.ppb.constants.SETTINGS_UPDATE, this.gameClient.receiveSettingsUpdate);
    this.gameClient.registerCallbackFunction(window.com.ppb.constants.NOTIFY_ACTION, this.gameClient.receiveNotifyAction);
    this.gameClient.registerCallbackFunction(window.com.ppb.constants.STOP_AUTO_SPIN, this.gameClient.receiveStopAutoSpin);

  }

  private puaseGame(timeout = 200): void {
    setTimeout(() => {
      this.rootStore.postMessageStore.sendPauseGame();
      this.rootStore.gameStore.setGamePaused(true);
    }, timeout);
  }

  private resumeGame(timeout = 200): void {
    setTimeout(() => {
      this.rootStore.postMessageStore.sendResumeGame();
      this.rootStore.gameStore.setGamePaused(false);
    }, timeout);
  }

  public updateAuthInfo(launchData: LaunchData | null) {
    if (!launchData) return;
    const { isAuthenticated, hasJackpot } = launchData;
    const authPayload = {
      name: ToGamePostMessage.AuthInfoUpdate,
      accessToken: this.getAccessToken(launchData),
      needHandleErrors: false,
      isAuthenticated,
      payload: { hasJackpot }
    };
    this.rootStore.postMessageStore.sendMessage(authPayload);
    if (this.rootStore.gameStore.launchData.data?.hasTournament) {
      this.rootStore.postMessageStore.sendTournamentMessage(authPayload);
    }
    if (this.rootStore.gameStore.launchData.data?.lobbyId) {
      this.rootStore.postMessageStore.sendLobbyMessage(authPayload);
    }
    if (this.rootStore.slotHistoryStore.isSlotHistoryOpen) {
      this.rootStore.postMessageStore.sendSlotHistoryMessage(authPayload);
    }
  }

  public handleErrorReceived(data: GameErrorPayload, refreshError = false): void {
    if (refreshError) {
      this.gameClient.sendErrorMessage("BGI-2000", "", {});
      return;
    }

    const errorCode = data.errorCode as number | string;
    const errorMessage = data.errorMessage;
    const betfaierErrorMessage: { errorCode: null | string, errorMessage: string } = {
      errorCode: null,
      errorMessage: ''
    };

    switch (errorCode) {
      case GameErrorCodes.GameIsOpen:
      case GameErrorCodes.ConnectionError:
        this.needReconnect = true;
        break;
    }
    
    if (data.operatorErrorCode) {
      betfaierErrorMessage.errorCode = `${data.operatorErrorCode}`;
    } else if (errorCode) {
      betfaierErrorMessage.errorCode = "BGI-" + (8000 + (errorCode as number));
      this.needReconnect = true;
    }

    if (errorMessage) {
      betfaierErrorMessage.errorMessage = errorMessage;
    }

    if (betfaierErrorMessage.errorCode && errorCode !== GameErrorCodes.Reconnect) {
      this.gameClient.sendErrorMessage(betfaierErrorMessage.errorCode, betfaierErrorMessage.errorMessage, {});
    }
  }

  public exitGame(): void {
    this.gameClient.sendEvent('exitGame', {});
  }

  public handleGameLoaded(launchData: LaunchData | null, stakeAmount?: number): void {
    if (launchData?.showPayTable) {
      this.rootStore.postMessageStore.sendOpenPayTable();
    }
    if (launchData?.licenseType === LicenseType.BrazilGLA) {
      this.rootStore.postMessageStore.sendIsBrazilGLA();
    }
    if (this.rootStore.sidebarMenuStore.showSidebar) {
      this.rootStore.sidebarMenuStore.sendSidebarPostMessage();
    }
    this.rootStore.postMessageStore.sendUpdateBalance();
    this.gameClient.sendGameLoadCompleted();
  }

  public handleRoundStarted(payload?: any): void {
    const currency = this.rootStore.gameStore.launchData.data?.currency;
    this.rootStore.gameStore.setRoundInProgress(true);
    this.gameClient.sendGameAnimationStart();
    this.gameClient.sendWinUpdate(0, currency);
  }

  public handleBalanceUpdated(payload?: any): void {
    const currency = this.rootStore.gameStore.launchData.data?.currency;
    this.gameClient.sendBalanceUpdate({ realAmount: payload?.balance }, currency);
  }

  public handleWinUpdate(payload?: any): void {
    const currency = this.rootStore.gameStore.launchData.data?.currency;
    this.gameClient.sendWinUpdate(payload?.win, currency);
  }

  public handleRoundFinished(payload: any) {
    if (this.rootStore.jackpotStore.openJackpotSceneAfterRoundFinished) {
      this.rootStore.jackpotStore.showJackpotScene();
      this.rootStore.jackpotStore.setOpenJackpotSceneAfterRoundFinished(false)
    }
    if (this.rootStore.boostedJackpotStore.openBoostedJackpotAfterRoundFinished) {
      this.rootStore.boostedJackpotStore.showBoostedJackpotWheel();
      this.rootStore.boostedJackpotStore.setOpenBoostedJackpotAfterRoundFinished(false);
    }
    this.rootStore.gameStore.setRoundInProgress(false);
    this.gameClient.sendGameAnimationCompleted();
    if (payload?.win) {
      this.handleWinUpdate(payload);
    }
  }

  public handleSettingsUpdated(payload?: { sound?: boolean, fastPlay?: boolean }) {
    this.gameClient.sendSettingsUpdate("sound", payload?.sound ? "on" : "off");
    this.gameClient.sendSettingsUpdate("fastPlay", payload?.fastPlay ? "on" : "off");
    if (payload?.hasOwnProperty('sound')) {
      this.rootStore.gameStore.handleSoundEnabledUpdate(!!payload?.sound);
    }
  }

  public handleStakeUpdate(payload?: any) {
    const currency = this.rootStore.gameStore.launchData.data?.currency;
    this.rootStore.gameStore.setStakeAmount(payload.stake);
    this.gameClient.sendStakeUpdate(Math.round(payload.stake * 100) / 100, currency);
  }

  public handlePlayModeUpdate(payload?: any) {
    if (payload?.playMode) {
      this.gameClient.sendPlayModeUpdate(payload.playMode);
    }
  }

}
